import { differenceInMinutes, isBefore, parseJSON } from 'date-fns/fp';
import type { Host, HostStatus } from '#dn-types/host.ts';

const STARTED_TRACKING_HOST_LAST_SEEN = parseJSON('2023-03-22T00:00:00Z');
const ONLINE_THRESHOLD_MINUTES_DNCLIENT = 2;
const ONLINE_THRESHOLD_MINUTES_MOBILE = 45;

type HostArg = Pick<Host, 'createdAt' | 'isBlocked'> & {
  metadata: Pick<Host['metadata'], 'lastSeenAt' | 'platform'>;
};

const createdBeforeTracking = isBefore(STARTED_TRACKING_HOST_LAST_SEEN);

export function computeHostStatus(
  { createdAt, isBlocked, metadata: { lastSeenAt, platform } }: HostArg,
  dataUpdatedAt: Date
): HostStatus {
  if (isBlocked) {
    return 'blocked';
  }
  if (!lastSeenAt && createdBeforeTracking(parseJSON(createdAt))) {
    return 'unknown';
  }
  if (!lastSeenAt) {
    return 'unenrolled';
  }
  if (isOnline(lastSeenAt, dataUpdatedAt, platform)) {
    return 'online';
  }
  return 'inactive';
}

/** {@link isOnline} returns a boolean about whether we consider the host "online" or not. 
This is different from the {@link HostStatus} in that a host can be "blocked" and "online" at the same time. */
function isOnline(lastSeenAt: string, dataUpdatedAt: Date, platform: string | null) {
  const minutesSinceLastSeen = differenceInMinutes(parseJSON(lastSeenAt), dataUpdatedAt);
  if (
    /** This assumes that {@link ONLINE_THRESHOLD_MINUTES_DNCLIENT} is less than {@link ONLINE_THRESHOLD_MINUTES_MOBILE} */
    minutesSinceLastSeen < ONLINE_THRESHOLD_MINUTES_DNCLIENT ||
    (platform === 'mobile' && minutesSinceLastSeen < ONLINE_THRESHOLD_MINUTES_MOBILE)
  ) {
    return true;
  }
  return false;
}
